
  import { defineComponent, ref, onMounted, computed, reactive } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import PackagesFilter from '@/components/dropdown/PackagesFilter.vue';

  export default defineComponent({
    name: 'supplier-packages-listing',
    components: {
      Datatable,
      PackagesFilter,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(async () => {
        setCurrentPageBreadcrumbs(translate('supplierPackages'), [
          translate('packages'),
        ]);
        loading.value = true;

        await store.dispatch(Actions.EXPORT_PACKAGES, 'supplier');
        loading.value = false;
      });

      const loading = ref(false);
      const tableHeader = ref([
        {
          name: translate('Shipment ID'),
          key: 'shipmentTrackNumber',
          sortable: true,
        },
        {
          name: translate('Package ID'),
          key: 'trackNumber',
          sortable: true,
        },
        {
          name: translate('customer name'),
          key: 'name',
          sortable: true,
        },
        {
          name: translate('recipientPhoneNumber'),
          key: 'recipientPhoneNumber',
          sortable: true,
        },
        {
          name: translate('city'),
          key: 'areaName',
          sortable: true,
        },
        {
          name: translate('passport Number'),
          key: 'passportNumber',
          sortable: true,
        },
        {
          name: translate('office Name'),
          key: 'officeName',
          sortable: true,
        },
        {
          name: translate('Driver Name'),
          key: 'driverName',
          sortable: true,
        },
        {
          name: translate('date'),
          key: 'createdAt',
          sortable: true,
        },
        {
          name: translate('status'),
          key: 'status',
          sortable: true,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);

      const formatDate = (date) => {
        return new Date(date).toISOString().slice(0, 10);
      };

      const moveToShipment = (id) => {
        router.push({ name: 'shipment-editing', params: { id } });
      };

      const excelHeader = reactive({
        'Shipment ID': 'shipmentTrackNumber',
        'Package ID': 'trackNumber',
        'Customer Name': 'name',
        'Phone Number': 'recipientPhoneNumber',
        City: 'areaName',
        'Driver Name': 'driverName',
        'Office Name': 'officeName',
        'Passport Number': 'passportNumber',
        Status: 'internalStatus',
        Date: {
          field: 'createdAt',
          callback: (value) => {
            return formatDate(value);
          },
        },
      });
      const deletePackages = async (packages) => {
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.DELETE_PACKAGE, packages.id);
          await store.dispatch(Actions.GET_PACKAGES, 'supplier');
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          }
        }
      };

      const onTypeChange = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_PACKAGES_FILTER, value);
        await store.dispatch(Actions.GET_PACKAGES, 'supplier');

        await store.dispatch(Actions.EXPORT_PACKAGES, 'supplier');
        setTimeout(() => {
          loading.value = false;
        }, 4000);

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        }
      };

      const onReset = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_PACKAGES_FILTER, value);
        await store.dispatch(Actions.GET_PACKAGES, 'supplier');
        await store.dispatch(Actions.EXPORT_PACKAGES, 'supplier');
        loading.value = false;
      };

      const overView = (id) => {
        router.push({ name: 'supplier-packages-overview', params: { id } });
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_PACKAGE_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_PACKAGES, 'supplier');
        loading.value = false;
      };

      return {
        tableData: computed(() => store.getters.packagesList),
        tableHeader,
        deletePackages,
        onTypeChange,
        onReset,
        translate,
        totalItems: computed(() => store.getters.packagesListCount),
        pageChanged,
        goTo,
        overView,
        moveToShipment,
        loading,
        can,
        excelHeader,
        excelData: computed(() => store.getters.exportPackagesList),
      };
    },
  });
